import { cn } from '@/utils/cn';
import bgVideoIcon from '@/assets/home/bg-video-icon.png';
import cq9Logo from '@/assets/home/cq9-logo.png';
import fgLogo from '@/assets/home/fg-logo.png';
import pgsoftLogo from '@/assets/home/pgsoft-logo.png';
import type { PlatformKey } from './homeThemes';

interface PlatformKeyVisualProps {
  platform: PlatformKey;
  className?: string;
}

export default function PlatformKeyVisual({ platform, className }: PlatformKeyVisualProps) {
  return (
    <div className={cn('relative h-[116px] w-[116px] overflow-hidden rounded-[18px]', className)}>
      {platform === 'ca28' && <Ca28Visual />}
      {platform === 'pg' && <PgVisual />}
      {platform === 'cq' && <CqVisual />}
      {platform === 'fg' && <FgVisual />}
      {platform === 'bg' && <BgVisual />}
    </div>
  );
}

function BgVisual() {
  return (
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(255,255,255,0.26),rgba(20,184,166,0.24)_28%,rgba(4,47,46,0.9)_74%,#031312_100%)]">
      <img
        src={bgVideoIcon}
        alt=""
        className="absolute inset-0 h-full w-full object-cover"
        draggable={false}
      />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.18),transparent_38%,rgba(0,0,0,0.18)_78%),radial-gradient(circle_at_24%_12%,rgba(255,255,255,0.2),transparent_22%)]" />
      <div className="pointer-events-none absolute inset-0 rounded-[18px] ring-1 ring-white/18" />
    </div>
  );
}

function Ca28Visual() {
  return (
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_44%_24%,#fff3a6_0%,#f97316_18%,#c31619_48%,#52040a_74%,#180106_100%)]">
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.2),transparent_35%,rgba(255,255,255,0.08)_65%,transparent),radial-gradient(circle_at_20%_12%,rgba(255,255,255,0.22),transparent_18%)]" />
      <div className="absolute left-1/2 top-[6%] h-[64%] w-[120%] -translate-x-1/2 rounded-full border border-amber-200/58 shadow-[inset_0_0_24px_rgba(251,191,36,0.48),0_0_24px_rgba(239,68,68,0.62)]" />
      <div className="absolute left-1/2 top-[10%] h-[48%] w-[78%] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_36%_25%,#fffbd1_0%,#ffc44d_35%,#f0442d_72%,#8b0f11_100%)] shadow-[0_0_30px_rgba(251,191,36,0.64),inset_0_8px_14px_rgba(255,255,255,0.35),inset_0_-10px_18px_rgba(81,8,10,0.45)]" />
      <div className="absolute left-1/2 top-[22%] -translate-x-1/2 text-[clamp(40px,12vw,60px)] font-black leading-none text-amber-50 drop-shadow-[0_5px_0_rgba(120,18,8,0.95)]">
        28
      </div>
      <div className="absolute bottom-[24%] left-1/2 -translate-x-1/2 rounded-full border border-yellow-100/70 bg-gradient-to-r from-amber-200 via-orange-300 to-yellow-400 px-4 py-0.5 text-[clamp(15px,4vw,20px)] font-black leading-none text-[#691500] shadow-[0_4px_10px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.72)]">
        CA28
      </div>
      <div className="absolute bottom-[7%] left-1/2 -translate-x-1/2 rounded-md border border-red-500/70 bg-black px-3 py-0.5 font-mono text-[clamp(16px,4.7vw,23px)] font-black leading-none text-red-500 shadow-[0_0_13px_rgba(239,68,68,0.78),inset_0_1px_0_rgba(255,255,255,0.12)]">
        00:58
      </div>
      <Ball className="left-[6%] top-[58%]" label="29" tone="from-orange-100 to-red-500" />
      <Ball className="right-[13%] top-[56%]" label="28" tone="from-blue-100 to-blue-600" />
      <Ball className="right-[3%] bottom-[16%]" label="3" tone="from-violet-100 to-violet-600" />
      <Coin className="left-[12%] top-[9%] rotate-[-24deg]" />
      <Coin className="right-[12%] top-[14%] rotate-[18deg]" />
      <Coin className="left-[3%] bottom-[14%] rotate-[20deg]" />
      <Coin className="right-[22%] bottom-[5%] rotate-[-12deg]" />
      <Spark className="left-[14%] top-[22%]" />
      <Spark className="right-[10%] top-[34%] scale-75" />
    </div>
  );
}

function PgVisual() {
  return (
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,rgba(255,255,255,0.95),rgba(245,243,255,0.9)_40%,rgba(91,33,182,0.68)_78%,#160f36_100%)]">
      <div className="absolute inset-x-2 top-[23%] rounded-lg bg-white/96 px-2 py-3 shadow-[0_10px_22px_rgba(24,12,72,0.28),inset_0_0_0_1px_rgba(88,28,135,0.08)]">
        <img
          src={pgsoftLogo}
          alt=""
          className="h-auto w-full object-contain"
          draggable={false}
        />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.28),transparent_36%,rgba(0,0,0,0.12)_76%),radial-gradient(circle_at_18%_14%,rgba(255,255,255,0.42),transparent_20%)]" />
      <div className="pointer-events-none absolute inset-0 rounded-[18px] ring-1 ring-white/22" />
    </div>
  );
}

function CqVisual() {
  return (
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,rgba(255,255,255,0.95),rgba(255,244,214,0.72)_38%,rgba(245,158,11,0.62)_78%,#3a1700_100%)]">
      <div className="absolute inset-x-2 top-[20%] rounded-lg bg-white/96 px-2 py-3 shadow-[0_10px_22px_rgba(120,53,15,0.28),inset_0_0_0_1px_rgba(217,119,6,0.1)]">
        <img
          src={cq9Logo}
          alt=""
          className="h-auto w-full object-contain"
          draggable={false}
        />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.28),transparent_36%,rgba(0,0,0,0.12)_76%),radial-gradient(circle_at_18%_14%,rgba(255,255,255,0.42),transparent_20%)]" />
      <div className="pointer-events-none absolute inset-0 rounded-[18px] ring-1 ring-white/22" />
    </div>
  );
}

function FgVisual() {
  return (
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_18%,rgba(255,255,255,0.9),rgba(125,211,252,0.46)_38%,rgba(7,84,184,0.75)_78%,#03112b_100%)]">
      <img
        src={fgLogo}
        alt=""
        className="absolute inset-2 h-[calc(100%-16px)] w-[calc(100%-16px)] object-contain drop-shadow-[0_10px_18px_rgba(2,6,23,0.42)]"
        draggable={false}
      />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.24),transparent_34%,rgba(0,0,0,0.12)_76%),radial-gradient(circle_at_18%_14%,rgba(255,255,255,0.36),transparent_22%)]" />
      <div className="pointer-events-none absolute inset-0 rounded-[18px] ring-1 ring-white/20" />
    </div>
  );
}

function Ball({ className, label, tone }: { className: string; label: string; tone: string }) {
  return (
    <div className={cn('absolute flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br text-[10px] font-black text-slate-950 shadow-[0_0_10px_rgba(255,255,255,0.35)]', tone, className)}>
      {label}
    </div>
  );
}

function Coin({ className }: { className: string }) {
  return (
    <div className={cn('absolute h-3 w-7 rounded-full border border-amber-100/70 bg-gradient-to-r from-yellow-200 via-orange-400 to-amber-600 shadow-[0_0_8px_rgba(251,191,36,0.65)]', className)} />
  );
}

function Spark({ className }: { className: string }) {
  return (
    <div className={cn('absolute h-4 w-4 rotate-45 bg-amber-200 shadow-[0_0_10px_rgba(251,191,36,0.8)] [clip-path:polygon(50%_0,62%_38%,100%_50%,62%_62%,50%_100%,38%_62%,0_50%,38%_38%)]', className)} />
  );
}
